<script setup lang="ts">
import { ref } from "vue";
import SearchableModel from "@/components/searchable-model/SearchableModel.vue";
import dayjs from "dayjs";
import { useBulkActionApi } from "@/composables/useBulkActionApi";
import useItemSelection from "@/composables/useItemSelection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn/components/ui/alert-dialog/index.js";
import { Button } from "@/shadcn/components/ui/button/index.js";
import { PackageCheckIcon, Undo2Icon } from "@lucide/vue";
import { toast } from "vue-sonner";

const props = defineProps({
  inventoryId: {
    type: Number,
    required: true,
  },
  bulkActionUrl: {
    type: String,
    required: true,
  },
  canManageEquipments: {
    type: Boolean,
    default: false,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const searchableModelRef = ref(null);
const {
  selectedIds,
  isSelected,
  select,
  deselect,
  deselectAll,
  updateAllSelection,
  selectedCount,
} = useItemSelection();

const showBulkConfirmation = ref(false);
const bulkAction = ref({
  label: "",
  action: "",
});

const onClickBulkAction = (label: string, action: string, equipmentId?: number) => {
  if (equipmentId) {
    deselectAll();
    select(equipmentId);
  }
  if (selectedIds.value.length === 0) {
    return;
  }
  showBulkConfirmation.value = true;
  bulkAction.value.label = label;
  bulkAction.value.action = action;
};

const { loading: waitingBulkResult, execute: executeBulkAction } = useBulkActionApi(props.bulkActionUrl);

const onConfirmBulkAction = async () => {
  if (waitingBulkResult.value) {
    return;
  }
  const response = await executeBulkAction(bulkAction.value.action, selectedIds.value, {
    inventory_id: props.inventoryId,
  });
  if (response.errors) {
    response.errors.forEach((error) => {
      toast.error(error);
    });
  }
  showBulkConfirmation.value = false;
  deselectAll();
  await searchableModelRef.value.getResults();
};

const onUpdateAllSelection = (models: any[], checked: boolean) => {
  updateAllSelection(checked, models);
};

const statusVariant = (value: string): string => {
  switch (value) {
    case "out":
      return "yellow";
    case "returned":
      return "green";
    case "missing":
      return "red";
    default:
      return "zinc";
  }
};
</script>

<template>
  <SearchableModel
    model-name="InventoryEquipment"
    with-search
    with-export
    load-on-start
    :search-placeholder="$t('Search by reference, barcode or serial number')"
    :base-filters="{ inventory_id: props.inventoryId }"
    enable-container
    ref="searchableModelRef"
  >
    <template #header-title>
      <slot name="header-title"></slot>
    </template>

    <template #header-actions>
      <slot name="header-actions"></slot>
    </template>

    <template #default="{ models }">
      <template v-if="canManageEquipments && isOpen && selectedCount > 0">
        <div class="d-print-none tw:flex tw:items-center tw:gap-2 tw:mb-4  tw:max-w-400  tw:mx-auto">
          <span>{{ $t("Bulk actions:") }}</span>
          <Button variant="outline" @click="onClickBulkAction($t('Check in'), 'inventory-check-in')">
            <PackageCheckIcon class="tw:w-3 tw:h-3" /> {{ $t("Check in") }}
          </Button>
          <Button variant="outline" @click="onClickBulkAction($t('Undo check-in'), 'inventory-undo-check-in')">
            <Undo2Icon class="tw:w-3 tw:h-3" /> {{ $t("Undo check-in") }}
          </Button>
          <span class="tw:ml-auto tw:text-zinc-500">
            {{ $t("Items selected", { count: selectedCount }, selectedCount) }}
          </span>
        </div>
      </template>

      <table v-if="models" class="table  tw:max-w-400 tw:mx-auto">
      <thead class="tw:text-left tw:text-sm tw:bg-zinc-50 tw:hidden tw:md:table-header-group">
        <tr>
          <th v-if="canManageEquipments && isOpen" class="d-print-none tw:w-px tw:whitespace-nowrap">
            <input
              type="checkbox"
              :checked="selectedCount === models?.length"
              :indeterminate="selectedCount > 0 && selectedCount < models?.length"
              @change="onUpdateAllSelection(models, $event.target.checked)"
            />
          </th>
          <th class="tw:text-left">{{ $t("Barcode") }}</th>
          <th class="tw:text-left">{{ $t("Model") }}</th>
          <th class="tw:text-left">{{ $t("Size") }} - {{ $t("Color") }}</th>
          <th class="tw:text-left">{{ $t("References") }}</th>
          <th class="tw:text-left">{{ $t("Status") }}</th>
          <th class="tw:text-left">{{ $t("Checked out at") }}</th>
          <th class="tw:text-left">{{ $t("Checked in at") }}</th>
          <th v-if="canManageEquipments && isOpen"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="equipment in models"
          :key="equipment.id"
          :class="{ 'tw:bg-amber-50': isSelected(equipment.id) }"
        >
          <td v-if="canManageEquipments && isOpen" class="d-print-none tw:w-px tw:whitespace-nowrap">
            <input
              type="checkbox"
              :checked="isSelected(equipment.id)"
              @change="isSelected(equipment.id) ? deselect(equipment.id) : select(equipment.id)"
            />
          </td>
          <td>{{ equipment.barcode_number }}</td>
          <td>{{ equipment.product_name }}</td>
          <td>
            {{ equipment.size }}<template v-if="equipment.size && equipment.color"> - </template>{{ equipment.color }}
          </td>
          <td>
            {{ equipment.serial_number }}
            <template v-if="equipment.reference && equipment.serial_number"><br /></template>
            {{ equipment.reference }}
          </td>
          <td>
            <dtc-badge :variant="statusVariant(equipment.inventory_status.value)">
              {{ equipment.inventory_status.label }}
            </dtc-badge>
          </td>
          <td class="tw:text-zinc-500">
            {{ equipment.checked_out_at ? dayjs(equipment.checked_out_at).format("lll") : "" }}
          </td>
          <td class="tw:text-zinc-500">
            {{ equipment.checked_in_at ? dayjs(equipment.checked_in_at).format("lll") : "" }}
          </td>
          <td v-if="canManageEquipments && isOpen" class="tw:whitespace-nowrap tw:text-right">
            <Button
              v-if="equipment.inventory_status.value === 'out'"
              variant="outline"
              size="sm"
              @click="onClickBulkAction($t('Check in'), 'inventory-check-in', equipment.id)"
            >
              <PackageCheckIcon class="tw:w-3 tw:h-3" /> {{ $t("Check in") }}
            </Button>
            <Button
              v-else-if="equipment.inventory_status.value === 'returned'"
              variant="outline"
              size="sm"
              @click="onClickBulkAction($t('Undo check-in'), 'inventory-undo-check-in', equipment.id)"
            >
              <Undo2Icon class="tw:w-3 tw:h-3" /> {{ $t("Undo check-in") }}
            </Button>
          </td>
        </tr>
      </tbody>
    </table>

    <AlertDialog :open="showBulkConfirmation" @update:open="showBulkConfirmation = $event">
      <AlertDialogContent class="tw:!max-w-sm tw:gap-6">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ bulkAction.label }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ $t("Are you sure you want to perform this bulk action on the selected models?") }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="secondary" @click="showBulkConfirmation = false" :disabled="waitingBulkResult">
              {{ $t("Cancel") }}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button @click="onConfirmBulkAction()" :loading="waitingBulkResult">
              {{ $t("Confirm") }}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </template>
  </SearchableModel>
</template>

<style scoped>
td {
  vertical-align: middle;
}
</style>
